import { onReady, REDUCE_MOTION } from "../base/common.js";

const normalizePath = (path) => {
    if (!path) return "/";
    const trimmed = path.replace(/\/+$/, "");
    return trimmed || "/";
};

const getHashFromLink = (link) => {
    const attr = link.getAttribute("href") || "";
    if (attr.startsWith("#")) return attr;
    try {
        return new URL(link.href).hash || "";
    } catch {
        return "";
    }
};

const getTargetFromHash = (hash) => {
    if (!hash || hash === "#") return null;
    const id = hash.replace(/^#/, "");
    if (!id) return null;
    return document.getElementById(id);
};

const TARGET_CLASS = "is-anchor-target";
let activeAnchorTarget = null;

const IS_IOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// transformされたpin要素でも変化しない、レイアウト上の絶対位置を取得する。
const getLayoutTop = (element) => {
    let top = 0;
    // ScrollTriggerのpin中は対象がposition: fixedになりoffsetParentを失う。
    // その場合も、本来の配置位置を保持しているpin-spacerを起点にする。
    let current = element.parentElement?.classList.contains("pin-spacer")
        ? element.parentElement
        : element;
    while (current) {
        top += current.offsetTop || 0;
        current = current.offsetParent;
    }
    return top;
};

const setAnchorTarget = (element) => {
    if (activeAnchorTarget === element) return element;
    activeAnchorTarget?.classList.remove(TARGET_CLASS);
    activeAnchorTarget = element || null;
    if (element) {
        element.classList.add(TARGET_CLASS);
    }
    return element;
};

onReady(() => {
    const header = document.querySelector(".l-header");
    const adminBar = document.getElementById("wpadminbar");
    let anchorRequestId = 0;

    const showHeader = (duration) => {
        if (!header) return;
        header.classList.remove("is-hide");
        document.dispatchEvent(
            new CustomEvent("header:force-show", {
                detail: { duration: typeof duration === "number" ? duration : 900 },
            })
        );
    };

    const getFixedOffset = () => {
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        const adminHeight = adminBar ? adminBar.getBoundingClientRect().height : 0;
        return headerHeight + adminHeight;
    };

    const prepareAnchorLayout = () => {
        document.dispatchEvent(new CustomEvent("anchor:prepare"));
    };

    const getDestination = (target) => Math.max(getLayoutTop(target) - getFixedOffset(), 0);

    const correctDestination = (target, requestId, attempts = 6, useVisualPosition = false) => {
        if (requestId !== anchorRequestId || attempts <= 0) return;
        requestAnimationFrame(() => {
            if (requestId !== anchorRequestId) return;
            if (!useVisualPosition) {
                prepareAnchorLayout();
            }
            const currentY = window.pageYOffset || window.scrollY || 0;
            const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
            const destination = useVisualPosition
                ? Math.min(
                      maxScroll,
                      Math.max(
                          0,
                          currentY + target.getBoundingClientRect().top - getFixedOffset()
                      )
                  )
                : getDestination(target);
            if (Math.abs(destination - currentY) > 1) {
                window.scrollTo({ top: destination, behavior: "auto" });
            }
            window.setTimeout(
                () => correctDestination(target, requestId, attempts - 1, true),
                180
            );
        });
    };

    const stabilizeDestination = (target, requestId, smooth) => {
        if (!smooth) {
            correctDestination(target, requestId);
            // iOS Safariの可変ツールバー反映後にも再確認する。
            window.setTimeout(() => correctDestination(target, requestId, 2), 1200);
            return;
        }

        let corrected = false;
        const correctAfterSmoothScroll = () => {
            window.removeEventListener("scrollend", correctAfterSmoothScroll);
            if (corrected || requestId !== anchorRequestId) return;
            corrected = true;
            correctDestination(target, requestId);
        };

        // pin区間をまたぐnative smooth scrollは開始位置で止まる場合があるため、
        // 終了後に最新レイアウトを使って着地点を確定する。
        window.addEventListener("scrollend", correctAfterSmoothScroll, { once: true });
        window.setTimeout(correctAfterSmoothScroll, 1800);
    };

    const scrollToHash = (hash, { smooth = true } = {}) => {
        const target = setAnchorTarget(getTargetFromHash(hash));
        if (!target) return;
        const requestId = ++anchorRequestId;
        prepareAnchorLayout();
        const currentY = window.pageYOffset || window.scrollY || 0;
        const destination = getDestination(target);
        const distance = Math.abs(destination - currentY);
        const holdDuration = Math.min(2000, Math.max(900, distance * 0.8));
        const useSmoothScroll = smooth && !REDUCE_MOTION && !IS_IOS;
        showHeader(holdDuration);
        window.scrollTo({
            top: Math.max(destination, 0),
            // iOS Safariはsmooth中に下部ツールバーでviewport高が変わるため即時移動する。
            behavior: useSmoothScroll ? "smooth" : "auto",
        });
        stabilizeDestination(target, requestId, useSmoothScroll);
    };

    const shouldHandleLink = (link) => {
        const href = link.getAttribute("href") || "";
        if (!href.includes("#")) return false;
        const hash = getHashFromLink(link);
        if (!hash || hash === "#" || hash.startsWith("#!")) return false;
        if (href.startsWith("#")) return true;

        try {
            const targetUrl = new URL(link.href);
            const currentUrl = new URL(window.location.href);
            return (
                targetUrl.origin === currentUrl.origin &&
                normalizePath(targetUrl.pathname) === normalizePath(currentUrl.pathname)
            );
        } catch {
            return false;
        }
    };

    document.querySelectorAll('a[href*="#"]').forEach((link) => {
        if (!shouldHandleLink(link)) return;

        link.addEventListener("click", (event) => {
            const hash = getHashFromLink(link);
            if (!hash) return;
            event.preventDefault();
            const runScroll = () => scrollToHash(hash);
            // ハンバーガーを閉じる際の scrollTo(lockedY) に潰されないよう1フレーム遅らせる
            if (document.documentElement.classList.contains("is-nav-open")) {
                requestAnimationFrame(() => requestAnimationFrame(runScroll));
            } else {
                runScroll();
            }
            if (window.history && window.history.pushState) {
                window.history.pushState(null, "", hash);
            } else {
                window.location.hash = hash;
            }
        });
    });

    if (window.location.hash) {
        requestAnimationFrame(() => scrollToHash(window.location.hash, { smooth: false }));
    }

    window.addEventListener("popstate", () => {
        const hash = window.location.hash;
        if (hash) {
            scrollToHash(hash, { smooth: false });
        } else {
            setAnchorTarget(null);
        }
    });
});
